import React, { useState } from 'react';
import PropTypes from 'prop-types';
import MaterialIcon from './MaterialIcon';
import MainHeader from './MainHeader';
import './FAQ.css';

const FAQ = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const faqSections = [
    {
      id: 'scene-builder',
      title: 'Scene Builder',
      icon: 'palette',
      questions: [
        {
          question: 'How do I use the Scene Builder?',
          answer: 'The Scene Builder lets you create custom sticker scenes. Browse stickers in the sidebar, click the "+" button to add them to your canvas, then drag and position them exactly where you want. You can change colors, sizes, and even rotate stickers to create the perfect scene.'
        },
        {
          question: 'Can I change sticker colors in the Scene Builder?',
          answer: 'Yes! Select any sticker on the canvas and use the color dropdown to choose from our 21 official ClothedEye colors including matte, metallic, and holographic options.'
        },
        {
          question: 'How do I buy the items in my scene?',
          answer: 'Due to Etsy\'s limitations, we cannot link directly to a product size or color, or add them directly to your cart. We can give you a shopping list and you can go to Etsy to purchase.'
        },
        {
          question: 'What sizes are available for each sticker?',
          answer: 'Available sizes vary by design but typically include ATOM, PIXEL, NANO, MICRO, SMALL, MEDIUM, and LARGE. Each size is precisely measured in inches and optimized for different applications.'
        }
      ]
    },
    {
      id: 'stickers',
      title: 'Sticker Information',
      icon: 'inventory_2',
      questions: [
        {
          question: 'What material are your stickers made from?',
          answer: 'Our stickers are made from high-quality outdoor vinyl that\'s weather-resistant, UV-resistant, and designed to last 5-7 years outdoors. They\'re perfect for cars, laptops, water bottles, and more.'
        },
        {
          question: 'Are your stickers waterproof?',
          answer: 'Yes! Our vinyl stickers are completely waterproof and can withstand rain, snow, and even car washes.'
        },
        {
          question: 'What\'s the difference between matte and metallic colors?',
          answer: 'Matte colors have a smooth, non-reflective finish that\'s perfect for most applications. Metallic colors (Silver, Gold, Copper) have a shiny, reflective finish that catches light beautifully.'
        }
      ]
    },
    {
      id: 'ordering',
      title: 'Ordering & Shipping',
      icon: 'local_shipping',
      questions: [
        {
          question: 'How do I place an order?',
          answer: 'Browse our collection, click on any sticker to see size and color options, then click "BUY IT NOW" to go directly to our Etsy shop where you can complete your purchase.'
        },
        {
          question: 'What sizes do you offer?',
          answer: 'We offer sizes from tiny ATOM decals (under 1 inch) up to LARGE formats. Each listing shows the exact dimensions in inches for every available size.'
        },
        {
          question: 'How long does shipping take?',
          answer: 'We typically ship within 1-2 business days. Domestic shipping usually takes 3-5 business days, while international orders take 1-3 weeks depending on location.'
        },
        {
          question: 'Do you ship internationally?',
          answer: 'Due to the high cost of international shipping, we currently only ship within the United States.'
        }
      ]
    },
    {
      id: 'technical',
      title: 'Technical Support',
      icon: 'settings',
      questions: [
        {
          question: 'I\'m having trouble with the website. What should I do?',
          answer: 'Try refreshing the page first. If problems persist, clear your browser cache or try a different browser. For ongoing issues, contact us through our Etsy shop.'
        },
        {
          question: 'The Scene Builder isn\'t loading properly.',
          answer: 'Make sure you have a stable internet connection and try refreshing the page. The Scene Builder works best on modern browsers like Chrome, Firefox, Safari, or Edge.'
        },
        {
          question: 'Can I use the Scene Builder on mobile?',
          answer: 'Yes! The Scene Builder is optimized for mobile devices with touch controls for dragging and positioning stickers.'
        }
      ]
    }
  ];

  return (
    <div className="faq-page">
      {/* Global Header with Navigation */}
      <MainHeader currentPage="faq" />

      {/* Page Title */}
      <div className="page-title">
        <h1>Frequently Asked Questions</h1>
        <p>Everything you need to know about ClothedEye stickers and the Scene Builder</p>
      </div>

      {/* FAQ Content */}
      <main className="faq-content">
        <div className="faq-container">
          {faqSections.map((section) => (
            <section key={section.id} className="faq-section">
              <button
                className={`section-header ${expandedSections[section.id] ? 'expanded' : ''}`}
                onClick={() => toggleSection(section.id)}
                aria-expanded={expandedSections[section.id]}
              >
                <div className="section-title">
                  <MaterialIcon icon={section.icon} size={24} />
                  <h2>{section.title}</h2>
                </div>
                <MaterialIcon 
                  icon={expandedSections[section.id] ? 'expand_less' : 'expand_more'} 
                  size={24} 
                />
              </button>
              
              {expandedSections[section.id] && (
                <div className="section-content">
                  {section.questions.map((qa, index) => (
                    <div key={index} className="qa-item">
                      <h3 className="question">{qa.question}</h3>
                      <p className="answer">{qa.answer}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
          
          {/* Contact Section */}
          <section className="contact-section">
            <div className="contact-header">
              <MaterialIcon icon="contact_support" size={32} />
              <h2>Still have questions?</h2>
            </div>
            <p>
              Can't find what you're looking for? Visit our{' '}
              <a 
                href="https://www.etsy.com/shop/ClothedEye?ref=seller-platform-mcnav#policies"
                target="_blank"
                rel="noopener noreferrer"
                className="etsy-link"
              >
                Etsy shop
              </a>
              {' '}to send us a message or check our shop policies.
            </p>
            <a
              href="https://www.etsy.com/shop/ClothedEye"
              target="_blank"
              rel="noopener noreferrer"
              className="contact-button"
            >
              <MaterialIcon icon="storefront" size={20} />
              Visit Our Etsy Shop
            </a>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="faq-footer">
        <p>&copy; 2024 Clothed Eye. All rights reserved.</p>
      </footer>
    </div>
  );
};

// FAQ has no props - it's a standalone page component
FAQ.propTypes = {};

export default FAQ;
